<template>
  <div class="row">
    <div class="col-md-12">
      <div class="block-items">
        <h2>Account Actions</h2>

        <b-button
          class="action button-item"
          size="sm"
          variant="info"
          @click="externalLink('meterRead')"
          >Submit Meter Reading</b-button
        >

        <div class="meter-note">
          <span>PLEASE NOTE:</span> Automatically recorded reads will be used by
          default, if available. When providing a meter read, the image
          submitted should contain the clear meter read, the whole meter and
          meter serial number if this is displayed.
        </div>

        <span v-if="isLease">
          <b-button
            class="action button-item"
            size="sm"
            variant="info"
            @click="externalLink('newLease')"
            >Change of Leaseholder</b-button
          >

          <b-button
            v-if="
              !pendingMoveInRequest &&
                (!activeSubLease || pendingMoveOutRequest)
            "
            class="action button-item"
            size="sm"
            variant="info"
            @click="openMoveInModal"
            >Move in Tenant</b-button
          >

          <b-button
            v-if="activeSubLease && !pendingMoveOutRequest"
            class="action button-item"
            size="sm"
            variant="info"
            @click="openMoveOutModal"
            >Move out Tenant</b-button
          >
        </span>

        <div class="pending-alerts">
          <b-alert v-if="pendingMoveOutRequest" variant="warning" show>
            <div class="title mb-3">
              <strong
                >You have requested a tenant move out on
                {{ pendingMoveOutRequest.submission_date }}, ({{
                  pendingMoveOutRequest.name
                }}
                - {{ pendingMoveOutRequestDate }}). This request is now
                pending.</strong
              >
              <span v-if="!pendingMoveInRequest">
                You can cancel this request below.</span
              >
              <span v-else>
                You are unable to cancel this request - please first cancel the
                move in</span
              >
            </div>
            <b-button
              v-if="!pendingMoveInRequest"
              variant="warning"
              class="mb-2"
              size="sm"
              @click="cancelMoveRequest('sub_end')"
              >Cancel</b-button
            >
          </b-alert>

          <b-alert v-if="pendingMoveInRequest" variant="warning" show>
            <div class="title mb-3">
              <strong
                >You have requested a tenant move in on
                {{ pendingMoveInRequest.submission_date }}, ({{
                  pendingMoveInRequest.name
                }}
                - {{ pendingMoveInRequestDate }}). This request is now
                pending.</strong
              >
              You can cancel this request below.
            </div>
            <b-button
              variant="warning"
              class="mb-2"
              size="sm"
              @click="cancelMoveRequest('sub_start')"
              >Cancel</b-button
            >
          </b-alert>
        </div>

        <h2>Further Help</h2>

        <div class="meter-note">
          <span>Help Centre</span>
        </div>

        <div class="meter-note">
          In our Help Centre you are able to access our extensive library of helpful FAQs, our Bills Support Page and find information on how to contact our Customer Care Team via livechat, over the phone or by submitting a ticket
        </div>

        <b-button
          class="action button-item"
          size="sm"
          variant="info"
          @click="externalLink('help')"
          >Help centre</b-button
        >

        <div class="meter-note">
          <span>Leaflets Library</span>
        </div>

        <div class="meter-note">
          In here you can browse through any of our helpful leaflets in one place
        </div>

        <b-button
          class="action button-item"
          size="sm"
          variant="info"
          @click="externalLink('leaflet')"
          >Leaflets Library</b-button
        >

        <div class="meter-note">
          <span>Newsletter</span>
        </div>

        <div class="meter-note">
          This newsletter brings together practical advice, important updates and a look ahead to what’s coming next. Please click below to subscribe!
        </div>

        <b-button
          class="action button-item"
          size="sm"
          variant="info"
          @click="externalLink('newsletter')"
        >Newsletter Signup</b-button
        >
      </div>
    </div>

    <b-modal
      id="move-out-modal"
      @show="resetMoveOutModal"
      @hidden="resetMoveOutModal"
      @ok="moveOutTenant"
      ok-title="Confirm"
      ok-variant="success"
      size="lg"
    >
      <template #modal-title>
        Move out Tenant
      </template>
      <div class="d-block">
        <b-row>
          <b-col class="col-12">
            <p class="request-instruct">
              The form below will instruct us to move out the existing tenant
              <strong v-if="activeTenant">({{ activeTenant }})</strong> using
              the details provided.
            </p>
          </b-col>
          <b-col class="col-4">
            <label for="move-out-date">Move out date:</label>
          </b-col>
          <b-col class="col-8">
            <input
              id="move-out-date"
              class="form-control"
              required
              v-model="moveOutDate"
              type="date"
            />
          </b-col>
        </b-row>

        <b-alert variant="warning" show v-if="moveOutDate > comparisonDate">
          <div class="title">
            <strong>NOTE: You are choosing a date in the future</strong>
          </div>
        </b-alert>

        <span>Enter your name below to confirm this request</span>
        <b-form-input
          class="mt-2 mb-3 input-large"
          v-model="submissionName"
          placeholder="Your name"
        ></b-form-input>
        <span class="request-date">Date of request: {{ dateToday }}</span>

        <ActionsAndHelpTerms></ActionsAndHelpTerms>

        <b-form-checkbox v-model="moveOutTerms" size="md"
          >I accept the above terms</b-form-checkbox
        >
      </div>

      <b-alert class="mt-3" v-if="errorText" variant="danger" show>
        <div><strong>Error</strong></div>
        {{ errorText }}
      </b-alert>
    </b-modal>
    <b-modal
      id="move-in-modal"
      @show="resetMoveInModal"
      @hidden="resetMoveInModal"
      @ok="moveInTenant"
      ok-title="Confirm"
      ok-variant="success"
      size="lg"
    >
      <template #modal-title>
        Move in Tenant
      </template>
      <div class="d-block">
        <b-row>
          <b-col class="col-12">
            <p class="request-instruct">
              The form below will instruct us to move in a new tenant using the
              details provided.
            </p>
          </b-col>
          <b-col class="col-4">
            <label for="move-in-date">Move in date:</label>
          </b-col>
          <b-col class="col-8">
            <input
              class="form-control"
              id="move-in-date"
              required
              v-model="moveInDate"
              type="date"
            />
          </b-col>
        </b-row>

        <b-alert variant="warning" show v-if="moveInDate > comparisonDate">
          <div class="title">
            <strong>NOTE: You are choosing a date in the future</strong>
          </div>
        </b-alert>

        <b-form-input
          class="mt-4 mb-3 input-large"
          v-model="tenantName"
          placeholder="Tenant name"
        ></b-form-input>

        <input
          required
          class="mt-4 mb-3 input-large form-control"
          v-model="tenantEmail"
          type="email"
          placeholder="Tenant email"
        />

        <span>Enter your name below to confirm this request</span>
        <b-form-input
          class="mt-2 mb-3 input-large"
          v-model="submissionName"
          placeholder="Your name"
        ></b-form-input>
        <span class="request-date">Date of request: {{ dateToday }}</span>

        <b-form-checkbox v-model="isLettingAgent" size="md" class="mt-3"
          >This is a letting agent</b-form-checkbox
        >

        <div v-if="isLettingAgent">
          <b-form-input
            class="mt-4 mb-3 input-large"
            v-model="agentName"
            placeholder="Letting agent name"
          ></b-form-input>
          <input
            required
            class="mt-4 mb-3 input-large form-control"
            v-model="agentEmail"
            type="email"
            placeholder="Letting agent email"
          />
          <b-form-input
            class="mt-4 mb-3 input-large"
            v-model="agentPhone"
            placeholder="Letting agent phone"
          ></b-form-input>
        </div>

        <p class="mt-3 meter-read-message">
          Please provide a meter read using our
          <span class="meter-read-link" @click="externalLink('meterRead')"
            >meter read submission form</span
          >
          to ensure accurate billing
        </p>

        <ActionsAndHelpTerms></ActionsAndHelpTerms>

        <b-form-checkbox v-model="moveInTerms" size="md"
          >I accept the above terms</b-form-checkbox
        >
      </div>

      <b-alert class="mt-3" v-if="errorText" variant="danger" show>
        <div><strong>Error</strong></div>
        {{ errorText }}
      </b-alert>
    </b-modal>
  </div>
</template>

<script>
import { externalLinks } from "@/components/mixins/external-links";
import { date } from "@/components/mixins/date";
import ActionsAndHelpTerms from "@/views/Billing/Components/ActionsAndHelpTerms.vue";

export default {
  name: "ActionsAndHelp",
  components: { ActionsAndHelpTerms },
  mixins: [externalLinks, date],
  props: {
    isLease: {
      type: Boolean,
      required: true
    },
    account: {
      type: String,
      required: true
    },
    development: {
      type: String,
      required: true
    },
    reference: {
      type: String,
      required: true
    },
    lastStatement: {
      type: String,
      default: null
    },
    activeSubLease: {
      type: Boolean,
      required: true
    },
    pendingMoveOutRequest: {
      type: Object,
      default: null
    },
    pendingMoveInRequest: {
      type: Object,
      default: null
    },
    activeTenant: {
      type: String,
      default: null
    }
  },
  computed: {
    pendingMoveInRequestDate() {
      return this.formatDate(
        this.pendingMoveInRequest.move_in_at,
        true
      ).replaceAll("-", "/");
    },
    pendingMoveOutRequestDate() {
      return this.formatDate(
        this.pendingMoveOutRequest.move_out_at,
        true
      ).replaceAll("-", "/");
    },
    comparisonDate() {
      return this.formatDate(new Date());
    },
    dateToday() {
      return this.formatDate(new Date(), true).replaceAll("-", "/");
    },
    statementTitle() {
      if (this.lastStatement) {
        return "You requested a statement on " + this.lastStatement;
      }

      return "Request statement";
    }
  },
  data() {
    return {
      loading: false,
      moveOutDate: null,
      moveInDate: null,
      moveInTerms: false,
      moveOutTerms: false,
      tenantName: null,
      tenantEmail: null,
      submissionName: null,
      errorText: null,
      isLettingAgent: false,
      agentName: null,
      agentEmail: null,
      agentPhone: null
    };
  },
  methods: {
    updateParent() {
      this.$emit("dashboard_refresh");
    },
    validateMoveInSubmit() {
      if (!this.errorText && !this.moveInDate) {
        this.errorText = "Please choose a move in date";
      }
      if (!this.errorText && !this.tenantName) {
        this.errorText = "Please enter tenant name";
      }
      if (!this.errorText && !this.submissionName) {
        this.errorText = "Please enter your name";
      }
      if (!this.errorText && !this.moveInTerms) {
        this.errorText = "Please accept the terms to continue";
      }
    },
    validateMoveOutSubmit() {
      if (!this.errorText && !this.moveOutDate) {
        this.errorText = "Please choose a move out date";
      }
      if (!this.errorText && !this.submissionName) {
        this.errorText = "Please enter your name";
      }
      if (!this.errorText && !this.moveOutTerms) {
        this.errorText = "Please accept the terms to continue";
      }
    },
    openMoveOutModal() {
      this.$bvModal.show("move-out-modal");
    },
    resetMoveOutModal() {
      this.moveOutDate = null;
      this.submissionName = null;
      this.errorText = null;
      this.moveOutTerms = false;
    },
    openMoveInModal() {
      this.$bvModal.show("move-in-modal");
    },
    resetMoveInModal() {
      this.moveInDate = null;
      this.tenantName = null;
      this.tenantEmail = null;
      this.submissionName = null;
      this.isLettingAgent = false;
      this.agentName = null;
      this.agentEmail = null;
      this.agentPhone = null;
      this.errorText = null;
      this.moveInTerms = false;
    },
    async moveOutTenant(bvModalEvent) {
      this.loading = true;
      bvModalEvent.preventDefault();

      this.errorText = null;

      this.validateMoveOutSubmit();

      if (this.errorText) {
        this.loading = false;
        return;
      }

      const id = this.reference;

      const data = {
        moveOutDate: this.moveOutDate,
        submissionName: this.submissionName
      };

      await this.$store
        .dispatch("dashboardMoveOutTenant", { id, data })
        .then(() => {
          this.updateParent();

          const title = "Request Received";
          const subtitle = "We have received your move out request";
          const samePage = true;
          this.$store.dispatch("pushSuccess", { title, subtitle, samePage });
          this.$emit("success");
        })
        .catch(error => {
          //Handled in error handler
          const title = "Error";
          const subtitle = error;
          const samePage = true;
          this.$store.dispatch("pushError", { title, subtitle, samePage });
          return false;
        })
        .finally(() => {
          this.loading = false;
          this.$bvModal.hide("move-out-modal");
        });
    },
    async moveInTenant(bvModalEvent) {
      this.loading = true;
      bvModalEvent.preventDefault();

      this.errorText = null;

      this.validateMoveInSubmit();

      if (this.errorText) {
        this.loading = false;
        return;
      }

      const id = this.reference;

      const data = {
        moveInDate: this.moveInDate,
        tenantName: this.tenantName,
        tenantEmail: this.tenantEmail,
        submissionName: this.submissionName,
        agentName: this.agentName,
        agentEmail: this.agentEmail,
        agentPhone: this.agentPhone
      };

      await this.$store
        .dispatch("dashboardMoveInTenant", { id, data })
        .then(() => {
          this.updateParent();

          const title = "Request received";
          const subtitle = "We have received your move in request";
          const samePage = true;
          this.$store.dispatch("pushSuccess", { title, subtitle, samePage });
          this.$emit("success");
        })
        .catch(error => {
          //Handled in error handler
          const title = "Error";
          const subtitle = error;
          const samePage = true;
          this.$store.dispatch("pushError", { title, subtitle, samePage });
          return false;
        })
        .finally(() => {
          this.loading = false;
          this.$bvModal.hide("move-in-modal");
        });
    },
    async cancelMoveRequest(pendingRequestType) {
      this.loading = true;

      const id = this.reference;

      const data = {
        pendingRequestType: pendingRequestType
      };

      await this.$store
        .dispatch("dashboardCancelMoveRequest", { id, data })
        .then(() => {
          this.updateParent();

          const title = "Success";
          const subtitle = "Move out request cancelled";
          const samePage = true;
          this.$store.dispatch("pushSuccess", { title, subtitle, samePage });
          this.$emit("success");
        })
        .catch(error => {
          //Handled in error handler
          const title = "Error";
          const subtitle = error;
          const samePage = true;
          this.$store.dispatch("pushError", { title, subtitle, samePage });
          return false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    requestStatement() {
      this.$swal({
        title: "Request a Statement",
        type: "info",
        input: "text",
        html:
          "<div><p>Request a billing statement from our customer services team</p></div>",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Request Statement",
        showLoaderOnConfirm: true,
        customClass: {
          input: "d-none"
        },
        preConfirm: () => {
          const id = this.reference;

          return this.$store
            .dispatch("dashboardCreditRequestStatement", { id })
            .then(() => {
              const title = "Statement Requested";
              const subtitle = "We have received your statement request";
              this.$store.dispatch("pushSuccess", { title, subtitle });

              this.updateParent();
            })
            .catch(err => {
              const title = "Invalid data";
              const subtitle = "There was a problem with your request: " + err;
              const samePage = true;
              this.$store.dispatch("pushError", { title, subtitle, samePage });
            });
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../scss/dashboard.scss";

.block-items {
  text-align: left;
}

.button-item {
  margin-top: -5px;
  margin-bottom: 20px;
  margin-left: 20px;
}

.meter-note {
  font-size: 0.9rem;
  margin: 10px 20px;
  color: #666;

  span {
    font-weight: bold;
  }
}

.pending-alerts {
  margin-top: 20px;
}

.request-date {
  font-size: 0.8rem;
  color: #777;
  margin-top: -8px;
  display: block;
}

.request-instruct {
  margin-top: -20px;
  font-size: 0.9rem;
  margin-bottom: 25px;
}

.meter-read-message {
  font-size: 0.9rem;
  color: #666;
}

.meter-read-link {
  color: #007bff;
  cursor: pointer;
}
</style>
